import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import jaJP from 'antd/lib/date-picker/locale/ja_JP';
import {
    Button,
    Input,
    Row,
    Col,
    Radio,
    Checkbox,
    Tooltip,
} from 'antd';
import {
    NotificationOutlined,
    TeamOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import {
    formRules,
    getDisabledDate,
    getMoment,
    getDefaultFeatures,
    DatePickerRangesIncludeInfinite,
    checkEnvironmentForControllable,
} from 'constants/GlobalConfig';
import { texts } from 'constants/texts'
import * as Actions from 'flex/Actions';
import { statusEnum } from 'flex/Actions';
import { allowedExtensions, FileUploader } from 'components/modules/FileUploader';
import CustomRangePicker from 'components/modules/CustomRangePicker';
import RichTextEditor from 'components/modules/RichTextEditor';
import { PublishedItemAlert } from 'components/modules/Announcement/PublishedItemAlert';
import BaseModal from 'components/modules/BaseModal';
import Spin from 'components/modules/Spin';
import Targets from '../Targets';
import NotificationPreviewModal from './NotificationPreviewModal';
import BaseForm from 'components/modules/BaseForm';
import { checkFormChange, convertDateToString, convertValuesTarget, decodeHtmlEntities, handleCheckInputLimitCharacter } from 'utils/Helper';

const Form = BaseForm.ModalForm;

const moment = getMoment();

const labelSpanXL = 7;
const labelSpanXXL = 6;

const layout = {
    labelCol: {
        sm: { span: 24 },
        md: { span: 24 },
        xl: { span: labelSpanXL },
        xxl: { span: labelSpanXXL },
    },
    wrapperCol: {
        span: 24,
    },
};

const LayoutWithoutLabel = {
    xl: { offset: labelSpanXL },
    xxl: { offset: labelSpanXXL },
};

const initFieldValue = {
    sender: '',
    title: '',
    duration: [
        moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
        undefined,
    ],
    content: '',
    files: [],
    priority: 1,
    push: 2,
    target: {
        target_users: [],
        target_roles: [],
        target_school_classes: [],
        target_organizations: []
    },
    publish_to_all_organizations: true,
    target_permissions: ['administrator', 'teacher'],
    can_admin_set_public: true
};


const EditNotification = ({
    onCancel,
    onOk,
    record,
    shouldCreate,
    visible,
}) => {
    const dispatch = useDispatch();
    const isControllableUser = useSelector(state => state.isControllableUser.payload);
    const [form] = Form.useForm();
    const [isSelectTargetOpen, setIsSelectTargetOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewFormData, setPreviewFormData] = useState(null);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [isPublishToAllOrganizations, setIsPublishToAllOrganizations] = useState(true);
    const [editableForCanAdminSetPublic, setEditableForCanAdminSetPublic] = useState(false);
    const [disabledForPublishedItem, setDisabledForPublishedItem] = useState(false);
    const [isDisabledSubmitBtn, setIsDisabledSubmitBtn] = useState(false);
    const [formInitValue, setFormInitValue] = useState({});
    const [formChangeValue, setFormChangeValue] = useState({});
    const target = Form.useWatch('target', form);
    const notificationEditConnection = useSelector(state => state.notificationEditConnection);
    const notificationViewConnection = useSelector(state => state.notificationViewConnection);
    const tenant = useSelector(state => state.tenant);

    const isFirstRender = useRef(false)

    const [detailLoading, setDetailLoading] = useState(false);

    const [, setFeatures] = useState(getDefaultFeatures());

    // Targets が各 key がないとエラーになるので, つけておく
    const currentTarget = {
        target_users: [],
        target_school_classes: [],
        target_roles: [],
        target_organizations: [],
        ...target,
    };

    useEffect(() => {
        if (tenant.payload?.features) {
            setFeatures(tenant.payload.features);
        }
    }, [tenant])

    useEffect(() => {
        isFirstRender.current = true;
    }, [])

    useEffect(() => {// 初期化・初期検索
        if (!visible) {// escで閉じた場合プレビューのisOpenがfalseにならないためfalseにしてあげる
            setIsPreviewOpen(false);
            return
        };
        if (!shouldCreate) {
            setIsDisabledSubmitBtn(true);
        }
        setDisabledForPublishedItem(false);
        setEditableForCanAdminSetPublic(false);
        setIsPublishToAllOrganizations(initFieldValue.publish_to_all_organizations);

        if (record === null) {
            form.resetFields();
            setContent('');
            form.setFieldsValue(initFieldValue);
        } else {
            if (isControllableUser) {
                dispatch(Actions.http.connection.notification.controlView(record.uuid));
            } else {
                dispatch(Actions.http.connection.notification.view(record.uuid));
            }
        }
    }, [record, visible]);// eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        if (!isFirstRender.current) {
            if (notificationViewConnection.meta.status === Actions.statusEnum.SUCCESS) {
                const result = notificationViewConnection.payload.result;
                const formValues = convertEditableRecord(result, shouldCreate);
                form.setFieldsValue(formValues);
                setContent(formValues.content);
                if (!shouldCreate) {
                    const formFieldValue = form.getFieldsValue();
                    const targetValue = !isControllableUser
                        ? convertValuesTarget(formFieldValue.target)
                        : formFieldValue.target;

                    setFormInitValue({
                        ...formFieldValue,
                        target: targetValue,
                        duration: convertDateToString(formFieldValue.duration),
                        content: decodeHtmlEntities(formFieldValue?.content ?? ''),
                    });
                }
                if (isControllableUser) {
                    setIsPublishToAllOrganizations(result.publish_to_all_organizations);
                } else {
                    setEditableForCanAdminSetPublic(result && result.can_admin_set_public);
                    setDisabledForPublishedItem(result && result.is_published_item);
                }
            }
            setDetailLoading(notificationViewConnection.meta.fetch);
        }
    }, [notificationViewConnection]);// eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        setLoading(notificationEditConnection.meta.fetch);
        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else {
            if (notificationEditConnection.meta.status === statusEnum.SUCCESS) {
                onOk();
            }
        }
    }, [notificationEditConnection]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!shouldCreate && visible) {
            formChangeValue.content = decodeHtmlEntities(formChangeValue?.content ?? '');
            const isFormChange = checkFormChange(formInitValue, formChangeValue);
            setIsDisabledSubmitBtn(!isFormChange);
        }
    }, [formChangeValue]);

    function handleSelectTargetSubmit(values) {
        setIsSelectTargetOpen(false);
        form.setFieldsValue({
            target: {
                target_users: values.target_users,
                target_school_classes: values.target_school_classes,
                target_roles: values.target_roles,
                target_organizations: values.target_organizations,
            }
        });
        handleValuesChange(form.getFieldsValue());
    }

    const handleSelectTargetCancel = () => setIsSelectTargetOpen(false);

    const onFinish = (values) => {
        setLoading(true);//最速ローディング
        const postBody = convertSubmittableValues(values);
        if (shouldCreate) {
            //新規作成(コピー含む)時
            if (isControllableUser) {
                dispatch(Actions.http.connection.notification.controlCreate(postBody));
            } else {
                dispatch(Actions.http.connection.notification.create(postBody));
            }
        } else {
            //編集時ccc
            if (isControllableUser) {
                dispatch(Actions.http.connection.notification.controlUpdate(record.uuid, postBody));
            } else {
                dispatch(Actions.http.connection.notification.update(record.uuid, postBody));
            }
        }
    }

    const handleModelChange = (value) => {
        setContent(value);
        form.setFieldsValue({
            content: value
        });
        handleValuesChange(form.getFieldsValue())
    };

    const handlePreviewOpen = () => {
        setPreviewFormData({ ...form.getFieldsValue(), content });
        setIsPreviewOpen(true);
    }

    const handlePreviewCancel = () => {
        setPreviewFormData(null);
        setIsPreviewOpen(false);
    }

    const handleSubmit = () => {
        if (!handleCheckInputLimitCharacter(form, ['content'])) {
            form.submit();
        }
    }

    // 学校管理者: 対象クラス選択&対象ユーザ選択, 全体管理者:対象学校選択のみ
    const targetShowTabProps = {
        showClass: !isControllableUser,
        showUser: !isControllableUser,
        showOrganization: isControllableUser,
        showRole: !isControllableUser
    }

    const handleCancel = () => {
        if (!shouldCreate) {
            setIsDisabledSubmitBtn(false);
        }
        onCancel();
    };

    const handleValuesChange = (valuesChange) => {
        if (!shouldCreate) {
            handleCheckInputLimitCharacter(form, ['content']);
            const targetValue = !isControllableUser
                ? convertValuesTarget(valuesChange.target)
                : valuesChange.target;

            setFormChangeValue({
                ...valuesChange,
                target: targetValue,
                duration: convertDateToString(valuesChange.duration),
            });
        }
    };

    return (
        <BaseModal
            className='common-modal'
            style={{ top: 20, width: '97vw !important' }}
            title={<ModalTitle record={record} shouldCreate={shouldCreate} />}
            visible={visible}
            onCancel={handleCancel}
            footer={[
                <Button key='preview' size='large' loading={loading} onClick={handlePreviewOpen}>プレビュー</Button>,
                <Button key='back' size='large' loading={loading} onClick={handleCancel}>キャンセル</Button>,
                <Button key='create' type='primary' size='large'
                    loading={loading}
                    disabled={!visible || (disabledForPublishedItem && !editableForCanAdminSetPublic) || isDisabledSubmitBtn}
                    onClick={handleSubmit}
                >
                    保存
                </Button>,
            ]}
            forceRender
            maskClosable={false}
        >
            <Spin spinning={detailLoading}>
                <PublishedItemAlert disabledForPublishedItem={disabledForPublishedItem} editableForCanAdminSetPublic={editableForCanAdminSetPublic} />

                <Form {...layout} form={form} name='control-hooks-edit-notifications' onFinish={onFinish}
                    onValuesChange={(_, allValues) => handleValuesChange(allValues)}
                >
                    <Form.Item name='title' label='タイトル'
                        rules={[
                            formRules.required({ label: 'タイトル' }),
                            formRules.range({ label: 'タイトル', max: 128 })
                        ]}
                    >
                        <Input placeholder='タイトルを入力してください' disabled={disabledForPublishedItem} />
                    </Form.Item>

                    <Form.Item name='publish_from' label='発信元'
                        rules={[
                            formRules.required({ label: '発信元' }),
                            formRules.range({ label: '発信元', max: 64 })
                        ]}
                    >
                        <Input placeholder='発信元を入力してください' disabled={disabledForPublishedItem} />
                    </Form.Item>

                    <Form.Item name='duration' label='公開期間'
                        rules={[
                            formRules.requiredDuration({ label: '公開期間' })
                        ]}
                    >
                        <CustomRangePicker
                            form={form}
                            name='duration'
                            locale={jaJP}
                            showTime={{ format: 'HH:mm' }}
                            format='YYYY/MM/DD HH:mm'
                            disabledDate={getDisabledDate}
                            ranges={DatePickerRangesIncludeInfinite()}
                            disabled={disabledForPublishedItem && !editableForCanAdminSetPublic}
                        />
                    </Form.Item>

                    <Form.Item name='content' label='内容'
                        rules={[
                            formRules.required({ label: '内容' }),
                        ]}
                    >
                        <RichTextEditor
                            tag='textarea'
                            config={{
                                charCounterMax: 10000,
                                placeholderText: '内容を入力してください',
                            }}
                            model={content}
                            onModelChange={handleModelChange}
                            disabled={disabledForPublishedItem}
                        />
                    </Form.Item>

                    <Form.Item name='files' label='ファイル'>
                        <FileUploader
                            disabled={disabledForPublishedItem}
                            allowedExtension={allowedExtensions.image_movie_zip_doc}
                            canUploadMultiFiles={true}
                        />
                    </Form.Item>

                    <Form.Item name='priority' label={<span>優先順位</span>}>
                        <Radio.Group disabled={disabledForPublishedItem}>
                            <Radio value={1}>通常</Radio>
                            <Radio value={2}>大切なお知らせ</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {isControllableUser ? (
                        <Form.Item name='publish_to_all_organizations' label='配信対象'>
                            <Radio.Group onChange={e => { setIsPublishToAllOrganizations(e.target.value); }} value={isPublishToAllOrganizations}>
                                <Radio value={true}>すべて</Radio>
                                <Radio value={false}>個別選択</Radio>
                            </Radio.Group>
                        </Form.Item>
                    ) : undefined}

                    <Form.Item name='target' value={currentTarget} hidden><Input /></Form.Item>

                    {(!isPublishToAllOrganizations || !isControllableUser) ? (
                        <Form.Item
                            wrapperCol={isControllableUser && LayoutWithoutLabel}
                            label={!isControllableUser && (<span>配信対象</span>)}
                        >
                            <Button type='primary' icon={<TeamOutlined />} onClick={() => setIsSelectTargetOpen(true)} disabled={disabledForPublishedItem}>
                                配信対象を選択
                            </Button>
                            <Targets.Detail currentTarget={currentTarget} isPublishedItem={disabledForPublishedItem} />
                        </Form.Item>
                    ) : undefined}

                    {isControllableUser ? (
                        <>
                            <Form.Item name='target_permissions'
                                label={texts.targetPermissionsTitle}
                                rules={[{ required: true, message: `${texts.targetPermissionsTitle}を選択してください` }]}
                            >
                                <Checkbox.Group style={{ width: '100%' }}>
                                    <Row>
                                        <Col sm={{ span: 7 }} md={{ span: 6 }} xl={{ span: 5 }}>
                                            <Checkbox value='administrator'>{texts.targetPermissionsCheckbox.administrator}</Checkbox>
                                        </Col>
                                        <Col sm={{ span: 7 }} md={{ span: 6 }} xl={{ span: 5 }}>
                                            <Checkbox value='teacher'>{texts.targetPermissionsCheckbox.teacher}</Checkbox>
                                        </Col>
                                        <Col sm={{ span: 7 }} md={{ span: 6 }} xl={{ span: 5 }}>
                                            <Checkbox value='student'>{texts.targetPermissionsCheckbox.student}</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span>
                                        学校管理画面での公開期間変更
                                        <Tooltip title='配信先の学校で、学校管理者が公開期間を任意に変更できるかどうかを設定できます'>
                                            <QuestionCircleOutlined className='icon-l' />
                                        </Tooltip>
                                    </span>
                                }
                            >
                                <Form.Item
                                    name='can_admin_set_public'
                                    style={{ display: 'inline-block', marginRight: '1rem' }}
                                    rules={[
                                        { required: true, message: '学校管理者での公開を選択してください' }
                                    ]}
                                >
                                    <Radio.Group>
                                        <Radio value={false}>許可しない</Radio>
                                        <Radio value={true}>許可する</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Form.Item>
                        </>
                    ) : undefined}

                    <Form.Item hidden name='inlineFiles'>
                        <input type='hidden' />
                    </Form.Item>

                </Form>
            </Spin>
            <Targets
                {...targetShowTabProps}
                visible={isSelectTargetOpen}
                currentTarget={currentTarget}
                onCancel={handleSelectTargetCancel}
                onSubmit={handleSelectTargetSubmit}
            />
            <NotificationPreviewModal
                isOpen={isPreviewOpen}
                closeModal={handlePreviewCancel}
                data={previewFormData}
            />
        </BaseModal>
    )
}

const ModalTitle = (props) => (
    props.shouldCreate
        ? props.record
            ? <span><NotificationOutlined /> お知らせをコピーして作成</span>
            : <span><NotificationOutlined /> お知らせの新規作成</span>
        : <span><NotificationOutlined /> お知らせの編集</span>
)

/***
 * targetを編集可能な形式に変更する
 * @param record
 */
const convertEditableTarget = (record) => ({
    target_school_classes: record.target_school_classes,
    target_organizations: record.target_organizations,
    target_users: record.target_users,
    target_roles: record.target_roles,
});

/***
 * recordを編集可能な形式に変更する
 * @param record
 */
const convertEditableRecord = (record, shouldCreate) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(record.content, 'text/html');
    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
        const src = img.getAttribute('src');
        const fileView = checkEnvironmentForControllable() ? '/control/file/view' : '/admin/file/view'
        img.setAttribute('src', src.replace(/\/file\/view/, fileView));
    });
    const processedContent = doc.body.innerHTML;

    return {
        ...record,
        content: processedContent,
        duration: [moment.unix(record.publish_start_at), moment.unix(record.publish_end_at)],
        files: record.files.filter(f => f.is_inline === false),
        is_send_notification: record.is_send_notification ? 1 : 2,
        target: convertEditableTarget(record),
        target_permissions: record.target_permissions && record.target_permissions.map(v => v?.code),
        title: shouldCreate ? `${record.title}のコピー` : record.title,
        inlineFiles: record.files.filter(f => f.is_inline === true).map(f => f.uuid),
    }
};

/***
 * submit可能な形式にonFinishのvalueを変換する
 */
const convertSubmittableValues = ({
    inlineFiles = [],
    target,
    duration,
    files,
    content,
    ...values
}) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
        const src = img.getAttribute('src');
        img.setAttribute('src', src.replace(/\/(control|admin)\/file\/view/, '/file/view'));
    });
    const processedContent = doc.body.innerHTML;

    const postBody = {
        ...values,
        content: processedContent,
        publish_department: '',
        publish_start_at: duration[0].unix(),
        publish_end_at: duration[1].unix(),
        is_read_receipt: false,
        is_send_notification: false,
        is_active: true,
        memo: '',
        files: files.map((value) => ({ file_uuid: value.uuid, is_inline: false })).concat(
            inlineFiles.map((uuid) => ({ file_uuid: uuid, is_inline: true }))
        ),
        target_users: target.target_users?.map((value) => value.uuid),
        target_school_classes: target.target_school_classes?.map((value) => value.uuid),
        target_roles: target.target_roles?.map((value) => value.uuid),
        target_organizations: target.target_organizations?.map((value) => value.uuid),
    };

    return postBody;
};

export default EditNotification;
